// @ts-nocheck - Suppress React 18/19 type compatibility warnings
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client/react';
import { configureAmplify } from './config/amplify';
import { createApolloClient } from './config/apollo';
import { SignIn } from './pages/SignIn';
import { Dashboard } from './pages/Dashboard';
import { InquiryQueue } from './pages/InquiryQueue';
import { InquiryDetail } from './pages/InquiryDetail';
import { CaseDetail } from './pages/CaseDetail';
import { CasesList } from './pages/CasesList';
import { ClientsList } from './pages/ClientsList';
import { ClientDetail } from './pages/ClientDetail';
import { UserCreate } from './pages/UserCreate';
import { UserEdit } from './pages/UserEdit';
import { MessagesList } from './pages/MessagesList';
import { DocumentsList } from './pages/DocumentsList';
import { InquiryIntakeForm } from './pages/InquiryIntakeForm';
import { TaskTemplates } from './pages/TaskTemplates';
import { TasksList } from './pages/TasksList';
import { Calendar } from './pages/Calendar';
import { CallLogs } from './pages/CallLogs';
import { VideoConferences } from './pages/VideoConferences';
import { VideoJoin } from './pages/VideoJoin';
import { VideoCall } from './pages/VideoCall';
import { MobileVideoCall } from './pages/MobileVideoCall';
import { GoogleCalendarCallback } from './pages/GoogleCalendarCallback';
import { Admin } from './pages/Admin';
import { ContactTrackingDashboard } from './pages/ContactTrackingDashboard';
import { UserManagement } from './pages/admin/UserManagement';
import { BulletinBoard } from './pages/BulletinBoard';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ClientProtectedRoute } from './client/components/ClientProtectedRoute';
import {
  DisabledAccountScreen,
  NewsScreen,
  CaseScreen,
  InquiryScreen,
  DocumentsScreen,
  ChatScreen,
  TasksScreen,
  CalendarScreen,
  ProfileScreen,
  AboutUsScreen,
} from './client/screens';
import './App.css';

// Configure Amplify
configureAmplify();

// Create Apollo Client
const apolloClient = createApolloClient();

function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/intake" element={<InquiryIntakeForm />} />
          <Route path="/inquiry" element={<InquiryIntakeForm />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/join/:token" element={<VideoJoin />} />
          <Route path="/mobile-video/:conferenceId" element={<MobileVideoCall />} />
          <Route path="/google-calendar-callback" element={<GoogleCalendarCallback />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inquiries"
            element={
              <ProtectedRoute>
                <InquiryQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inquiries/:id"
            element={
              <ProtectedRoute>
                <InquiryDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases"
            element={
              <ProtectedRoute>
                <CasesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases/:id"
            element={
              <ProtectedRoute>
                <CaseDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <ClientsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/new"
            element={
              <ProtectedRoute>
                <UserCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <ProtectedRoute>
                <ClientDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id/edit"
            element={
              <ProtectedRoute>
                <UserEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/call-logs"
            element={
              <ProtectedRoute>
                <CallLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/video-conferences"
            element={
              <ProtectedRoute>
                <VideoConferences />
              </ProtectedRoute>
            }
          />
          <Route
            path="/video-conferences/:conferenceId"
            element={
              <ProtectedRoute>
                <VideoCall />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <DocumentsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TasksList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/templates"
            element={
              <ProtectedRoute>
                <TaskTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulletin-board"
            element={
              <ProtectedRoute>
                <BulletinBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contact-tracking"
            element={
              <ProtectedRoute>
                <ContactTrackingDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contact-tracking"
            element={
              <ProtectedRoute>
                <ContactTrackingDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/new"
            element={
              <ProtectedRoute>
                <UserCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:id/edit"
            element={
              <ProtectedRoute>
                <UserEdit />
              </ProtectedRoute>
            }
          />
          {/* Client Portal Routes - wrapped in ClientProtectedRoute */}
          <Route
            path="/client/disabled"
            element={
              <ClientProtectedRoute>
                <DisabledAccountScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/news"
            element={
              <ClientProtectedRoute>
                <NewsScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/case"
            element={
              <ClientProtectedRoute>
                <CaseScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/inquiry"
            element={
              <ClientProtectedRoute>
                <InquiryScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/documents"
            element={
              <ClientProtectedRoute>
                <DocumentsScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/chat"
            element={
              <ClientProtectedRoute>
                <ChatScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/chat/:staffId"
            element={<Navigate to="/client/chat" replace />}
          />
          <Route
            path="/client/tasks"
            element={
              <ClientProtectedRoute>
                <TasksScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/calendar"
            element={
              <ClientProtectedRoute>
                <CalendarScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/profile"
            element={
              <ClientProtectedRoute>
                <ProfileScreen />
              </ClientProtectedRoute>
            }
          />
          <Route
            path="/client/about"
            element={
              <ClientProtectedRoute>
                <AboutUsScreen />
              </ClientProtectedRoute>
            }
          />
          {/* Default client route redirect */}
          <Route
            path="/client"
            element={<Navigate to="/client/news" replace />}
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </ApolloProvider>
  );
}

export default App;
